import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Scroll to top on route change, or to the hash target for deep-links like /#quote
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

// Pages
import Home from "./pages/Home";
import FinalExpense from "./pages/FinalExpense";
import Medicare from "./pages/Medicare";
import LifeInsurance from "./pages/LifeInsurance";
import HealthSupplemental from "./pages/HealthSupplemental";
import Agents from "./pages/Agents";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Testimonials from "./pages/Testimonials";
import ThankYou from "./pages/ThankYou";
import AgentLogin from "./pages/AgentLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AgentCareers from "./pages/AgentCareers";
import AgentDashboard from "./pages/AgentDashboard";
import WeeklyGoals from "./pages/WeeklyGoals";
import ReportsHistory from "./pages/ReportsHistory";
import AgentPerformance from "./pages/AgentPerformance";
import AdminReports from "./pages/AdminReports";
import AdminUsers from "./pages/AdminUsers";
import AdvancedQuote from "./pages/AdvancedQuote";
import QuotingTool from "./pages/QuotingTool";
import AdminTeams from "./pages/AdminTeams";
import AdminMetrics from "./pages/AdminMetrics";
import AdminAuditLog from "./pages/AdminAuditLog";
import AdminNotifications from "./pages/AdminNotifications";
import AdminUnlockRequests from "./pages/AdminUnlockRequests";
import AdminSettings from "./pages/AdminSettings";
import LeadManagement from "./pages/LeadManagement";
import AgentSettings from "./pages/AgentSettings";
import AgentBilling from "./pages/AgentBilling";
import BillingSuccess from "./pages/BillingSuccess";
import TrainingContent from "./pages/TrainingContent";
import VerifyEmail from "./pages/VerifyEmail";
import DailyActivity from "./pages/DailyActivity";
import IULs from "./pages/IULs";
import Annuities from "./pages/Annuities";
import Dental from "./pages/Dental";
import Vision from "./pages/Vision";
import PortalHome from "./pages/PortalHome";

const AppLayout = () => {
  const location = useLocation();
  const isPortal = location.pathname.startsWith('/portal') ||
    location.pathname.startsWith('/agent/dashboard') ||
    location.pathname.startsWith('/agent/goals') ||
    location.pathname.startsWith('/agent/daily-activity') ||
    location.pathname.startsWith('/agent/reports') ||
    location.pathname.startsWith('/agent/performance') ||
    location.pathname.startsWith('/agent/leads') ||
    location.pathname.startsWith('/agent/quote-tool') ||
    location.pathname.startsWith('/agent/settings') ||
    location.pathname.startsWith('/agent/billing') ||
    location.pathname.startsWith('/agent/training') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/admin/');

  return (
    <>
      <ScrollToTop />
      {!isPortal && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/final-expense" element={<FinalExpense />} />
        <Route path="/medicare" element={<Medicare />} />
        <Route path="/life-insurance" element={<LifeInsurance />} />
        <Route path="/health-supplemental" element={<HealthSupplemental />} />
        <Route path="/iuls" element={<IULs />} />
        <Route path="/annuities" element={<Annuities />} />
        <Route path="/dental" element={<Dental />} />
        <Route path="/vision" element={<Vision />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/careers" element={<AgentCareers />} />
        <Route path="/quote-tool" element={<AdvancedQuote />} />
        <Route path="/agent/login" element={<AgentLogin />} />
        <Route path="/agent/forgot-password" element={<ForgotPassword />} />
        <Route path="/agent/reset-password" element={<ResetPassword />} />
        <Route path="/agent/verify-email" element={<VerifyEmail />} />
        <Route path="/portal" element={<PortalHome />} />
        <Route path="/agent/register/dot" element={<AgentLogin defaultTeam="DOT" defaultTab="register" />} />
        <Route path="/agent/register/hig" element={<AgentLogin defaultTeam="HIG" defaultTab="register" />} />
        <Route path="/agent/dashboard" element={<AgentDashboard />} />
        <Route path="/agent/goals" element={<WeeklyGoals />} />
        <Route path="/agent/daily-activity" element={<DailyActivity />} />
        <Route path="/agent/reports" element={<ReportsHistory />} />
        <Route path="/agent/performance" element={<AgentPerformance />} />
        <Route path="/admin" element={<AdminReports />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/teams" element={<AdminTeams />} />
        <Route path="/admin/metrics" element={<AdminMetrics />} />
        <Route path="/admin/audit-log" element={<AdminAuditLog />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        <Route path="/admin/unlock-requests" element={<AdminUnlockRequests />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/agent/leads" element={<LeadManagement />} />
        <Route path="/admin/leads" element={<LeadManagement />} />
        <Route path="/agent/quote-tool" element={<QuotingTool />} />
        <Route path="/admin/quote-tool" element={<QuotingTool />} />
        <Route path="/agent/settings" element={<AgentSettings />} />
        <Route path="/agent/billing" element={<AgentBilling />} />
        <Route path="/agent/billing/success" element={<BillingSuccess />} />
        <Route path="/agent/training" element={<TrainingContent />} />
      </Routes>
      {!isPortal && <Footer />}
      <Toaster />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </div>
  );
}

export default App;
